/**
 * Vocabulaire des types d'événement côté front — libellé + couleur.
 * Aligné sur le CHECK SQL de la table `events` (backend lib/event-types.ts).
 *
 * Deux générations coexistent volontairement (cf. backend) : les 7 valeurs V1
 * sans accent, orientées cave & restaurant, et les valeurs V2 bar/cocktails
 * ajoutées pour Mayday. Ne pas « harmoniser » : des lignes en prod portent les
 * valeurs V1.
 *
 * Ce module est la source de vérité partagée par le sheet staff
 * (components/staff/eventTypes.ts, qui le ré-exporte) et les écrans clients.
 */

export type EventType =
  // ── V1 — cave & restaurant ──
  | 'degustation'
  | 'soiree'
  | 'masterclass'
  | 'privatisation'
  | 'fermeture'
  | 'concert'
  | 'autre'
  // ── V2 — bar à cocktails / vie de soirée ──
  | 'soirée-cocktails'
  | 'dégustation-spiritueux'
  | 'blind-test'
  | 'afterwork'
  | 'live-music'
  | 'karaoke'
  | 'quiz'
  | 'dj'
  | 'atelier'
  | 'brunch'
  | 'apéro-thématique'
  | 'lancement';

export interface EventTypeConfig {
  key: EventType;
  label: string;
  /** Couleur sobre par type — badge + fallback cover si pas d'image. */
  color: string;
}

export const EVENT_TYPES: EventTypeConfig[] = [
  { key: 'degustation', label: 'Dégustation', color: '#6b1f2a' },   // bordeaux / vin profond
  { key: 'soiree', label: 'Soirée', color: '#6d28d9' },             // violet / mauve
  { key: 'masterclass', label: 'Masterclass', color: '#0f766e' },   // teal foncé
  { key: 'privatisation', label: 'Privatisation', color: '#b08600' }, // doré
  { key: 'fermeture', label: 'Fermeture', color: '#64748b' },       // gris sobre
  { key: 'concert', label: 'Concert', color: '#c2410c' },           // terracotta / orange chaud
  { key: 'soirée-cocktails', label: 'Soirée cocktails', color: '#c026d3' },
  { key: 'dégustation-spiritueux', label: 'Dégustation spiritueux', color: '#92400e' },
  { key: 'blind-test', label: 'Blind test', color: '#0284c7' },
  { key: 'afterwork', label: 'Afterwork', color: '#db2777' },
  { key: 'live-music', label: 'Live music', color: '#7c3aed' },
  { key: 'karaoke', label: 'Karaoké', color: '#e11d48' },
  { key: 'quiz', label: 'Quiz', color: '#059669' },
  { key: 'dj', label: 'DJ set', color: '#4338ca' },
  { key: 'atelier', label: 'Atelier', color: '#15803d' },
  { key: 'brunch', label: 'Brunch', color: '#d97706' },
  { key: 'apéro-thématique', label: 'Apéro thématique', color: '#7e22ce' },
  { key: 'lancement', label: 'Lancement', color: '#0891b2' },
  { key: 'autre', label: 'Autre', color: '#7AB5D9' },               // bleu primaire tenant
];

const BY_KEY: Record<string, EventTypeConfig> = EVENT_TYPES.reduce(
  (acc, t) => { acc[t.key] = t; return acc; },
  {} as Record<string, EventTypeConfig>,
);

export function eventTypeConfig(type: EventType): EventTypeConfig {
  return BY_KEY[type] ?? BY_KEY.autre;
}

/**
 * Variante tolérante pour le flux PUBLIC : les entrées héritées d'agenda.json
 * portent des `type` libres (le blob n'a jamais eu de contrainte). Un type
 * inconnu est humanisé plutôt qu'écrasé en « Autre », qui perdrait l'info.
 */
export function publicTypeConfig(type: string | null | undefined): EventTypeConfig {
  if (!type) return BY_KEY.autre;
  const known = BY_KEY[type];
  if (known) return known;
  const label = type.replace(/[-_]+/g, ' ').replace(/^./, (ch) => ch.toUpperCase());
  return { key: 'autre', label, color: BY_KEY.autre.color };
}

// ── Badges publics — charte SI/Mayday ────────────────────────────────────────

/**
 * Style de badge d'un type d'événement, côté CLIENT.
 *
 * Distinct de `EventTypeConfig.color`, qui reste la couleur saturée utilisée par
 * les écrans staff (pastilles denses sur fond clair) : ici on veut une paire
 * fond/texte lisible telle quelle, pour un badge posé sur une card blanche.
 *
 * Les valeurs sont des `var(--color-*)` de la charte de l'app
 * (styles/global.css) — PAS des hex. Deux raisons : les badges suivent
 * automatiquement toute évolution de la charte, et le jour où un tenant
 * surcharge réellement ses `--color-*` (aujourd'hui `tenants/<id>.json` n'est lu
 * qu'au build pour le manifest PWA, jamais injecté en CSS), les badges suivent
 * sans retouche. Les strings `var(…)` fonctionnent en style inline React.
 *
 * Registre volontairement restreint : bleus de la charte (primary / mid / dark /
 * light / ultra-light), neutres, et le bordeaux historique du badge
 * « Dégustation ». Trois niveaux d'intensité pour que 5 cards à la suite ne
 * fassent pas un sapin de Noël :
 *   - fond plein + texte blanc → types « forts » (dégustation, soirée, live)
 *   - fond clair + encre       → types détente / jour
 *   - fond neutre + gris       → ludique et hors-programmation
 *
 * Les DEUX générations de valeurs (V1 sans accent, V2 bar à cocktails) mappent
 * volontairement sur le même couple : `soiree` et `soirée-cocktails` sont le
 * même objet produit, seule la saisie diffère.
 */
export interface EventTypeStyle {
  /** Fond du badge (et de la vignette quand il n'y a pas de cover). */
  bg: string;
  /** Couleur du texte posé dessus — jamais à recalculer côté écran. */
  fg: string;
  /** Libellé FR en sentence case (« Soirée cocktails », pas « SOIRÉE COCKTAILS »). */
  label: string;
}

const WHITE = '#ffffff';
const INK = 'var(--color-text)';

const STYLE_BY_KEY: Record<string, { bg: string; fg: string }> = {
  // Vin & spiritueux — le bordeaux historique de SI, inchangé.
  degustation: { bg: 'var(--color-bordeaux)', fg: WHITE },
  'dégustation-spiritueux': { bg: 'var(--color-bordeaux)', fg: WHITE },
  masterclass: { bg: 'var(--color-bordeaux)', fg: WHITE },
  // Soirée — remplace le violet #6d28d9, hors charte, par le bleu primaire.
  soiree: { bg: 'var(--color-primary)', fg: WHITE },
  'soirée-cocktails': { bg: 'var(--color-primary)', fg: WHITE },
  lancement: { bg: 'var(--color-primary)', fg: WHITE },
  // Live / musique / DJ — l'ardoise de la charte, plus sombre que le primaire.
  concert: { bg: 'var(--color-primary-dark)', fg: WHITE },
  'live-music': { bg: 'var(--color-primary-dark)', fg: WHITE },
  dj: { bg: 'var(--color-primary-mid)', fg: WHITE },
  // Détente & convivial jour — bleu clair + encre.
  'apéro-thématique': { bg: 'var(--color-primary-light)', fg: INK },
  afterwork: { bg: 'var(--color-primary-light)', fg: INK },
  atelier: { bg: 'var(--color-primary-light)', fg: INK },
  brunch: { bg: 'var(--color-primary-light)', fg: INK },
  // Ludique — bleu très pâle, le moins appuyé des fonds colorés.
  karaoke: { bg: 'var(--color-primary-ultra-light)', fg: 'var(--color-primary-mid)' },
  quiz: { bg: 'var(--color-primary-ultra-light)', fg: 'var(--color-primary-mid)' },
  'blind-test': { bg: 'var(--color-primary-ultra-light)', fg: 'var(--color-primary-mid)' },
  privatisation: { bg: 'var(--color-primary-ultra-light)', fg: 'var(--color-primary-mid)' },
  // Hors programmation : neutre, ça n'est pas une invitation.
  fermeture: { bg: 'var(--color-bg-subtle)', fg: 'var(--color-text-secondary)' },
};

/** Repli des types inconnus (agenda.json a toujours accepté du texte libre). */
const FALLBACK_STYLE = { bg: 'var(--color-primary-ultra-light)', fg: 'var(--color-primary-mid)' };

export function getEventTypeStyle(type: string | null | undefined): EventTypeStyle {
  const { label } = publicTypeConfig(type);
  const style = (type && STYLE_BY_KEY[type]) || FALLBACK_STYLE;
  return { bg: style.bg, fg: style.fg, label };
}
